import { useEffect, useState } from "react";
import { ArrowLeft, Sparkles, Share2, Copy, MessageCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, BACKEND_URL } from "../lib/api";
import CleaningScope from "../components/CleaningScope";
import { Brand, brand } from "../context/CompanyContext";

function resolveImg(item) {
  const u = item.image_url || item.src;
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return BACKEND_URL + u;
}

/**
 * Gallery — marketing page showcasing real cleaning results.
 * Fetches photos from `/api/gallery` (public). Admin manages them in
 * `/admin/galeria`. Accessible to every role and to unauthenticated
 * visitors (for sharing / trust-building).
 */
export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const q = activeCat !== "all" ? `?category=${encodeURIComponent(activeCat)}` : "";
    setLoading(true);
    api.get(`/gallery${q}`)
      .then(({ data }) => {
        // Admin can hide the public gallery via /admin/promocao → Galeria pública
        if (data.enabled === false) {
          setDisabled(true);
          setPhotos([]);
          return;
        }
        setDisabled(false);
        setPhotos(data.items || []);
        if (data.categories && data.categories.length) setCategories(data.categories);
      })
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, [activeCat]);

  const catLabels = {
    quarto: "Quartos",
    cozinha: "Cozinhas",
    casa_de_banho: "Casas de banho",
    sala: "Salas",
    detalhes: "Detalhes",
    outros: "Outros",
  };

  const shareUrl = typeof window !== "undefined" ? window.location.origin + "/galeria" : "https://luxstay.pt/galeria";
  const shareText = `Veja como a equipa ${brand()} cuida dos alojamentos locais na Madeira — gestão profissional de limpezas.`;

  function copyLink() {
    navigator.clipboard?.writeText(shareUrl).then(
      () => toast.success("Link copiado! Cole onde quiser partilhar."),
      () => toast.error("Não foi possível copiar. Selecione manualmente."),
    );
  }

  function shareWhatsApp() {
    const text = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  }

  async function shareNative() {
    if (!navigator.share) { copyLink(); return; }
    try {
      await navigator.share({ title: `${brand()} · Galeria`, text: shareText, url: shareUrl });
    } catch {
      // user cancelled — silent
    }
  }

  return (
    <div className="min-h-screen bg-[#010c0a] text-[#fdfbf7]" data-testid="gallery-page">
      <div className="max-w-6xl mx-auto px-4 py-10 md:py-16">
        <button onClick={() => nav(-1)}
                className="text-xs text-[#a0b2af] hover:text-[#d4af37] inline-flex items-center gap-2 mb-8"
                data-testid="gallery-back">
          <ArrowLeft size={14} /> Voltar
        </button>

        <header className="mb-12">
          <div className="eyebrow flex items-center gap-2 text-[#d4af37]">
            <Sparkles size={14} /> Galeria
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">
            O trabalho que nos orgulha.
          </h1>
          <p className="text-sm md:text-base text-[#a0b2af] mt-4 max-w-2xl leading-relaxed">
            Cada limpeza é uma oportunidade de elevar a experiência do hóspede.
            Veja alguns exemplos do que a equipa <Brand short /> entrega em alojamentos
            locais da Madeira — do rigor operacional ao cuidado com os detalhes
            que o anfitrião não tem tempo para ver.
          </p>

          <div className="flex flex-wrap gap-2 mt-6" data-testid="gallery-share">
            <button onClick={shareWhatsApp}
                    className="px-4 py-2 text-xs uppercase tracking-widest border border-[#10b981]/40 text-[#10b981] hover:bg-[#10b981]/10 transition-colors inline-flex items-center gap-2"
                    data-testid="gallery-share-whatsapp">
              <MessageCircle size={14} /> Partilhar no WhatsApp
            </button>
            <button onClick={shareNative}
                    className="px-4 py-2 text-xs uppercase tracking-widest border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 transition-colors inline-flex items-center gap-2"
                    data-testid="gallery-share-native">
              <Share2 size={14} /> Partilhar
            </button>
            <button onClick={copyLink}
                    className="px-4 py-2 text-xs uppercase tracking-widest border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors inline-flex items-center gap-2"
                    data-testid="gallery-share-copy">
              <Copy size={14} /> Copiar link
            </button>
          </div>
        </header>

        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="gallery-filters">
            {[{ key: "all", label: "Todas" }, ...categories.map((c) => ({
              key: c, label: catLabels[c] || c,
            }))].map((c) => (
              <button key={c.key}
                      onClick={() => setActiveCat(c.key)}
                      className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                        activeCat === c.key
                          ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
                          : "border-[#1a4640] text-[#a0b2af] hover:text-[#fdfbf7] hover:border-[#fdfbf7]/30"
                      }`}
                      data-testid={`gallery-filter-${c.key}`}>
                {c.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-[#a0b2af]">A carregar galeria…</div>
        ) : disabled ? (
          <div className="lux-card p-10 text-center max-w-lg mx-auto"
               data-testid="gallery-disabled-msg">
            <div className="eyebrow mb-3">Galeria temporariamente indisponível</div>
            <p className="text-sm text-[#a0b2af]">
              A nossa galeria está em pausa. Pedimos desculpa pelo incómodo —
              entretanto pode contactar-nos diretamente para ver exemplos do nosso trabalho.
            </p>
            <Link to="/" className="inline-block mt-5 text-xs uppercase tracking-widest text-[#d4af37] hover:underline">
              ← Voltar à página inicial
            </Link>
          </div>
        ) : photos.length === 0 ? (
          <div className="lux-card p-8 text-center text-sm text-[#a0b2af]">
            A galeria ainda está vazia.
          </div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {photos.map((p, i) => (
            <button key={p.id || i}
                    onClick={() => setSelected(p)}
                    className="group text-left"
                    data-testid={`gallery-item-${i}`}>
              <div className="relative overflow-hidden bg-[#062c27] aspect-[4/3] border border-[#1a4640] group-hover:border-[#d4af37]/60 transition-colors">
                <img src={resolveImg(p)} alt={p.title}
                     loading="lazy"
                     decoding="async"
                     className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="mt-3">
                <div className="font-display text-lg group-hover:text-[#d4af37] transition-colors">{p.title}</div>
                <p className="text-xs text-[#a0b2af] mt-1 leading-relaxed">{p.caption}</p>
              </div>
            </button>
          ))}
        </div>
        )}

        <div className="mt-12">
          <CleaningScope testId="gallery-scope" />
        </div>

        <div className="mt-16 lux-card p-8 text-center border-[#d4af37]/40">
          <div className="font-display text-2xl">Ainda não é cliente?</div>
          <p className="text-sm text-[#a0b2af] mt-2 max-w-lg mx-auto">
            Crie a sua conta gratuita e agende a primeira limpeza em menos de um minuto.
          </p>
          <Link to="/register" data-testid="gallery-cta-register"
                className="inline-block mt-5 btn-gold px-6 py-2.5 text-xs uppercase tracking-widest">
            Criar conta gratuita
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      {selected && (
        <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm p-4 flex items-center justify-center"
             onClick={() => setSelected(null)}
             data-testid="gallery-lightbox">
          <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <img src={resolveImg(selected)} alt={selected.title}
                 className="w-full max-h-[70vh] object-contain" />
            <div className="mt-4 px-2">
              <div className="font-display text-2xl text-[#d4af37]">{selected.title}</div>
              <p className="text-sm text-[#a0b2af] mt-1">{selected.caption}</p>
            </div>
            <button onClick={() => setSelected(null)}
                    className="absolute top-4 right-4 text-[#fdfbf7] hover:text-[#d4af37] text-2xl"
                    data-testid="gallery-lightbox-close">
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
